// The tab's token reads: what extensions get from $tp.auth.getAccessToken(), what the API interceptor
// and startup send (requestToken, requestTokenFor), and the framed page's lookups on the top window.
// No imports: the app bundle loads this file and must not pull in oidc-client-ts with it.

// A token with less than this left is renewed before it is handed out.
export const RENEW_SKEW_SECONDS = 30;

// The signed-in user, renewed when the token is about to expire and the tab can renew it. When it
// can't, or the renewal fails, the stored user comes back as it is, possibly expired. Never throws.
export async function currentUser(userManager) {
    let user;
    try {
        user = await userManager.getUser();
    } catch {
        return null;
    }
    if (!user) return null;
    if (user.expires_in === undefined || user.expires_in > RENEW_SKEW_SECONDS) return user;
    // Without a refresh token the renewal is the hidden frame; a token that is still valid goes out
    // now rather than waiting on it.
    if (!user.refresh_token && user.expires_in > 0) return user;
    if (!userManager.canRenew?.(user)) return user;
    try {
        return (await userManager.signinSilent()) ?? user;
    } catch {
        return user;
    }
}

// The API interceptor's token: the current user's, or null when the tab has to sign in again. That is
// when nobody is signed in, or the token has expired and can't be renewed (no refresh token, or the STS
// refused it). An expired token whose renewal failed some other way (network, timeout) goes out as it is,
// so a click while the network is still coming back fails like any offline call instead of leaving the app.
export async function requestToken(userManager) {
    const user = await currentUser(userManager);
    if (!user) return null;
    if (user.expired && (!user.refresh_token || !userManager.canRenew?.(user))) return null;
    return user.access_token;
}

// A framed page asks the top window for tokens, so only one window in the tab ever renews.
const topAuthFunction = (win, name) => {
    try {
        if (win.self === win.top) return null;
        return win.top.$tp?.auth?.[name] ?? null;
    } catch {
        return null;
    }
};

export const topAccessToken = (win) => topAuthFunction(win, "getAccessToken");

// requestToken for this page: through the top window when framed and it has one, else the page's own
// manager (which can't renew in a frame). Null means sign in again.
export async function requestTokenFor(win, userManager) {
    const top = topAuthFunction(win, "requestToken");
    if (top) {
        try {
            return await top();
        } catch {
            // fall through to the page's own read
        }
    }
    return requestToken(userManager);
}

// getAccessToken for this page: through the top window when framed and it has one, else the page's own
// read. A failing top window never fails the read.
export async function getAccessTokenFor(win, userManager) {
    const top = topAccessToken(win);
    if (top) {
        try {
            return await top();
        } catch {
            // fall through to the page's own read
        }
    }
    return getAccessToken(userManager);
}

export async function getAccessToken(userManager) {
    return (await currentUser(userManager))?.access_token ?? null;
}
